// Single source of truth for the on-disk driver format. Drivers are
// pure profile data — name, identity, and skill attributes the AI
// controller will consume later. No physics here. All attributes are
// 0..1 floats so the simulator can map them into per-driver behavior
// without further normalization.
//
// Forward compatibility: new attribute fields added in a future
// version get a default in DRIVER_ATTR_DEFAULTS; old saves load
// cleanly and round-trip without losing existing fields.

export const FORMAT_VERSION = 1;

// Skill attributes. 0 = beginner, 1 = peak professional. Defaults are
// neutral mid-pack so a partially-authored driver still drives.
export const DRIVER_ATTR_DEFAULTS = {
  pace:           0.50,  // raw qualifying speed
  consistency:    0.50,  // lap-to-lap variance (lower = steadier)
  racecraft:      0.50,  // wheel-to-wheel decisions
  tyreManagement: 0.50,  // long-stint pace falloff
  wetWeather:     0.50,  // grip-loss tolerance
  aggression:     0.50,  // 0 = patient, 1 = always pushing
};

export function newEmptyDriver() {
  return {
    formatVersion: FORMAT_VERSION,
    name: '',
    shortName: '',           // 3-letter abbrev, e.g. "DOE"
    nationality: '',         // ISO 3166-1 alpha-3 (optional, free-form OK)
    preferredNumber: null,   // optional int 1-99
    attributes: { ...DRIVER_ATTR_DEFAULTS },
    metadata: { createdAt: null, modifiedAt: null },
  };
}

// Validate + normalize a parsed driver object. Returns
// { driver, formatMismatch }.
export function loadDriver(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('Not a valid driver file: not an object');
  }
  if (typeof data.formatVersion !== 'number') {
    throw new Error('Not a valid driver file: missing formatVersion');
  }
  if (typeof data.name !== 'string' || data.name.trim() === '') {
    throw new Error('Not a valid driver file: missing name');
  }

  const merged = newEmptyDriver();
  Object.assign(merged, data);

  merged.attributes = { ...DRIVER_ATTR_DEFAULTS, ...(data.attributes ?? {}) };
  if (typeof merged.shortName !== 'string') merged.shortName = '';
  if (typeof merged.nationality !== 'string') merged.nationality = '';
  if (typeof merged.preferredNumber !== 'number') merged.preferredNumber = null;
  if (!merged.metadata) merged.metadata = { createdAt: null, modifiedAt: null };

  // Clamp attributes to [0, 1]. Out-of-range values likely mean a
  // file was hand-edited; clamping is friendlier than rejecting.
  for (const k of Object.keys(merged.attributes)) {
    const v = merged.attributes[k];
    if (typeof v !== 'number' || !Number.isFinite(v)) {
      merged.attributes[k] = DRIVER_ATTR_DEFAULTS[k] ?? 0.5;
    } else {
      merged.attributes[k] = Math.max(0, Math.min(1, v));
    }
  }

  return {
    driver: merged,
    formatMismatch: data.formatVersion > FORMAT_VERSION ? data.formatVersion : null,
  };
}
